/**
 * Kétoszlopos konfigurátor: bal = brány vizualizáció, jobb = űrlap.
 * Fejléc: JEDNODUCHO A RÝCHLO + piros címsor.
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import ConfiguratorPreview from "./ConfiguratorPreview";
import ConfiguratorForm, {
  type ConfiguratorFormProps,
  type ConfiguratorProductOption,
  type PriceCombination,
} from "./ConfiguratorForm";

export interface ConfiguratorLayoutProps extends Omit<ConfiguratorFormProps, "motiv" | "surface" | "color" | "onMotivChange" | "onSurfaceChange" | "onColorChange"> {
  configuratorProductOptions?: ConfiguratorProductOption[];
  priceCombinations?: PriceCombination[];
  configuratorMotivOrder?: string[];
}

function getFirstSelection(options: ConfiguratorProductOption[], motivOrder?: string[]): { motiv: string; surface: string; color: string } {
  if (options.length === 0) return { motiv: "m", surface: "woodgrain", color: "" };
  let firstMotiv: string;
  if (motivOrder && motivOrder.length > 0) {
    firstMotiv = motivOrder[0];
  } else {
    const allMotivs = [...new Set(options.flatMap((o) => o.motivs))];
    allMotivs.sort((a, b) => (a === "m" ? -1 : b === "m" ? 1 : 0));
    firstMotiv = allMotivs[0] ?? "m";
  }
  const optionsForMotiv = options.filter((o) => o.motivs.includes(firstMotiv));
  const optionForSurface = optionsForMotiv[0];
  const firstSurface = optionForSurface?.surface ?? options[0].surface;
  const firstColor = optionForSurface?.colors[0]?.code ?? "";
  return { motiv: firstMotiv, surface: firstSurface, color: firstColor };
}

/** RAL/CH stb. kód normalizálása: pl. "CH 8028", "8028" -> "8028", hogy povrch-ök között egyezzen a megőrzés */
function normalizeColorCode(code: string): string {
  const t = code.trim();
  const match = t.match(/\d+/);
  return match ? match[0] : t;
}

function colorMatches(a: string, b: string): boolean {
  if (a === b) return true;
  return normalizeColorCode(a) === normalizeColorCode(b);
}

export default function ConfiguratorLayout(props: ConfiguratorLayoutProps) {
  const { t } = useTranslation();
  const options = props.configuratorProductOptions ?? [];
  const motivOrder = props.configuratorMotivOrder;
  const [motiv, setMotiv] = useState("m");
  const [surface, setSurface] = useState("woodgrain");
  const [color, setColor] = useState("");
  const initialSelectionDoneRef = useRef(false);

  useEffect(() => {
    if (options.length === 0) {
      initialSelectionDoneRef.current = false;
      return;
    }
    if (initialSelectionDoneRef.current) return;
    const currentOption = options.find((o) => o.surface === surface && o.motivs.includes(motiv));
    const hasValidColor = currentOption?.colors.some((c) => colorMatches(c.code, color));
    const nothingSelected = color === "" || !currentOption || !hasValidColor;
    if (!nothingSelected) {
      initialSelectionDoneRef.current = true;
      return;
    }
    const first = getFirstSelection(options, motivOrder);
    setMotiv(first.motiv);
    setSurface(first.surface);
    setColor(first.color);
    initialSelectionDoneRef.current = true;
  }, [options, motiv, surface, color, motivOrder]);

  useEffect(() => {
    if (options.length === 0) return;
    const optionsForMotiv = options.filter((o) => o.motivs.includes(motiv));
    const currentInMotiv = optionsForMotiv.some((o) => o.surface === surface);
    if (currentInMotiv) return;
    const first = optionsForMotiv[0];
    if (first) {
      setSurface(first.surface);
      setColor(first.colors[0]?.code ?? "");
    }
  }, [motiv, options, surface]);

  // Povrch váltásakor: ha az új povrch nem támogatja a jelenlegi motívet, váltsunk érvényes motívra.
  // Ha a jelenlegi szín (pl. 9005) elérhető az új povrch-nál, maradjon kijelölve.
  const handleSurfaceChange = (newSurface: string) => {
    const optionsForSurface = options.filter((o) => o.surface === newSurface);
    const optionForCurrentMotiv = optionsForSurface.find((o) => o.motivs.includes(motiv));
    if (optionForCurrentMotiv) {
      const matchingColor = optionForCurrentMotiv.colors.find((c) => colorMatches(c.code, color));
      if (!matchingColor && optionForCurrentMotiv.colors.length > 0) {
        setColor(optionForCurrentMotiv.colors[0].code);
      } else if (matchingColor && matchingColor.code !== color) {
        setColor(matchingColor.code);
      }
      setSurface(newSurface);
      return;
    }
    if (optionsForSurface.length > 0) {
      const first = optionsForSurface[0];
      setMotiv(first.motivs[0] ?? "m");
      const matchingInFirst = first.colors.find((c) => colorMatches(c.code, color));
      setColor(matchingInFirst ? matchingInFirst.code : (first.colors[0]?.code ?? ""));
    }
    setSurface(newSurface);
  };

  const overlayFilename =
    props.configuratorProductOptions?.find((o) => o.surface === surface)?.colors.find((c) => colorMatches(c.code, color))
      ?.overlayFilename ?? null;

  // Mobilnézet: sticky kapu görgetéskor akár 30%-kal zsugorodik
  const scrollRef = useRef<HTMLDivElement>(null);
  const stickyRef = useRef<HTMLDivElement>(null);
  const [stickyScale, setStickyScale] = useState(1);
  const scrollTopWhenStuckRef = useRef<number | null>(null);
  const SCROLL_FOR_MAX_SHRINK = 220;
  const MIN_SCALE = 0.7;
  const FADE_HEIGHT_PX = 32;
  /** Kapu alatti tiszta fehér sáv (px), csak utána kezdődik a ködösítés */
  const WHITE_STRIP_BELOW_DOOR_PX = 2;
  /** Alulsó zónában ennyi px marad tiszta fehér, csak ez alatt kezdődik a köd (hogy a kapu alatt is fehér menjen le a csíkjáig) */
  const WHITE_ABOVE_STRIP_PX = 8;
  const TOTAL_BOTTOM_ZONE_PX = FADE_HEIGHT_PX + WHITE_STRIP_BELOW_DOOR_PX;
  /** Kapu megjelenítési magasság (a ködös zóna fölött), hogy mindig teljes egészében látszódjon – nincs clip. */
  const DOOR_AREA_HEIGHT_PX = 280 - TOTAL_BOTTOM_ZONE_PX;

  const updateStickyScale = useCallback(() => {
    const scrollEl = scrollRef.current;
    const stickyEl = stickyRef.current;
    if (!scrollEl || !stickyEl) return;
    const stickyRect = stickyEl.getBoundingClientRect();
    const containerRect = scrollEl.getBoundingClientRect();
    const isStuck = stickyRect.top <= containerRect.top + 2;
    if (isStuck) {
      if (scrollTopWhenStuckRef.current === null) {
        scrollTopWhenStuckRef.current = scrollEl.scrollTop;
      }
      const extraScroll = scrollEl.scrollTop - scrollTopWhenStuckRef.current;
      const t = Math.min(1, extraScroll / SCROLL_FOR_MAX_SHRINK);
      setStickyScale(1 - (1 - MIN_SCALE) * t);
    } else {
      scrollTopWhenStuckRef.current = null;
      setStickyScale(1);
    }
  }, []);

  useEffect(() => {
    const scrollEl = scrollRef.current;
    if (!scrollEl) return;
    scrollEl.addEventListener("scroll", updateStickyScale, { passive: true });
    return () => scrollEl.removeEventListener("scroll", updateStickyScale);
  }, [updateStickyScale]);

  const isEmbed = Boolean(props.embedMode);
  const layoutRootRef = useRef<HTMLDivElement>(null);

  return (
    <div
      ref={layoutRootRef}
      className={
        isEmbed
          ? "configurator-layout flex flex-col flex-1 min-h-0 w-full"
          : "configurator-layout flex flex-col min-h-[100dvh] h-full min-h-0 w-full"
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_453px] flex-1 min-h-0 w-full">
        {/* Bal: kapu vizualizáció – desktop only; mobilnézetben a jobb oszlopban sticky */}
        <div className="hidden lg:flex order-2 lg:order-1 min-h-0 items-center justify-center overflow-hidden bg-white">
          <ConfiguratorPreview
            motiv={motiv}
            surface={surface}
            colorCode={color}
            overlayFilename={overlayFilename}
            className="w-full max-w-2xl"
          />
        </div>
        {/* Jobb: fejléc + mobilnézetben sticky kapu + görgethető űrlap – ez a scroll container mobilnézetben */}
        <div
          ref={scrollRef}
          className={`order-1 lg:order-2 w-full flex flex-col min-h-0 ${isEmbed ? "h-full" : "max-h-full lg:max-h-[100dvh]"} overflow-y-auto border-l-2 border-border bg-background`}
          style={{ boxShadow: "inset 4px 0 12px -4px rgba(0,0,0,0.08)" }}
        >
          <header className="shrink-0" style={{ padding: "20px 25px 0 25px" }}>
            <div className="flex items-center gap-3 mb-[11px]">
              <span className="configurator-deco-line" />
              <p
                className="text-[15px] uppercase tracking-normal"
                style={{ fontFamily: "Roboto, Arial, Helvetica, sans-serif", color: "var(--configurator-form-text)" }}
              >
                {t("configurator.subtitle", "JEDNODUCHO A RÝCHLO")}
              </p>
            </div>
            <h1 style={{ fontSize: "48px", fontFamily: "'Saira Condensed', 'Sarabun', Arial, Helvetica, sans-serif", fontWeight: 700, letterSpacing: "-0.02em", lineHeight: 1.2 }}>
              <span className="block" style={{ color: "var(--configurator-form-text)" }}>
                {t("configurator.titleLine1", "DAJTE SI VYPRACOVAŤ")}
              </span>
              <span className="block" style={{ color: "var(--configurator-warm-accent)" }}>
                {t("configurator.titleLine2", "CENOVÚ PONUKU!")}
              </span>
            </h1>
          </header>
          {/* Mobilnézet: sticky kapu – teljes szélességű fehér háttér, belső kép skálázódik; magasság csökken; alul átlátszó gradiens + árnyék, hogy az űrlap puha ködösítéssel tűnjön el */}
          <div
            ref={stickyRef}
            className="lg:hidden shrink-0 sticky top-0 z-30 w-full overflow-hidden relative"
            style={{
              height: `${Math.round(DOOR_AREA_HEIGHT_PX * stickyScale + TOTAL_BOTTOM_ZONE_PX)}px`,
              transition: "height 150ms ease-out",
              /* Fehér a tetejétől; alul TOTAL_BOTTOM_ZONE_PX: fehér megy le WHITE_ABOVE_STRIP_PX-ig, majd rövid köd, majd 2px fehér csík */
              background: `linear-gradient(to bottom, #ffffff 0%, #ffffff calc(100% - ${TOTAL_BOTTOM_ZONE_PX}px), #ffffff calc(100% - ${WHITE_ABOVE_STRIP_PX}px), transparent calc(100% - ${WHITE_STRIP_BELOW_DOOR_PX}px), #ffffff calc(100% - ${WHITE_STRIP_BELOW_DOOR_PX}px), #ffffff 100%)`,
              boxShadow: "0 4px 20px -4px rgba(0,0,0,0.08)",
            }}
          >
            <div
              className="w-full flex items-center justify-center pt-[2px]"
              style={{
                height: `${DOOR_AREA_HEIGHT_PX}px`,
                transform: `scale(${stickyScale})`,
                transformOrigin: "top center",
                transition: "transform 150ms ease-out",
              }}
            >
              <ConfiguratorPreview
                motiv={motiv}
                surface={surface}
                colorCode={color}
                overlayFilename={overlayFilename}
                className="w-full max-w-2xl"
                containInHeight={DOOR_AREA_HEIGHT_PX}
              />
            </div>
          </div>
          <div className="flex-1" style={{ padding: "4px 25px 32px 25px" }}>
            <ConfiguratorForm
              {...props}
              configuratorMotivOrder={motivOrder}
              motiv={motiv}
              surface={surface}
              color={color}
              onMotivChange={setMotiv}
              onSurfaceChange={handleSurfaceChange}
              onColorChange={setColor}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
